export class ApiError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
  }

  name = 'ApiError';
  httpCode?: number;
  body?: string;
  serverMessage?: string;

  get isNotFound() {
    return this.httpCode === 404;
  }

  get isBadRequest() {
    return this.httpCode === 400;
  }

  get isUnauthorized() {
    return this.httpCode === 401;
  }

  get isForbidden() {
    return this.httpCode === 403;
  }

  get isConflict() {
    return this.httpCode === 409;
  }

  get isFetchError() {
    return this.httpCode === undefined;
  }

  static fromResponseData(apiName: string, url: string, status: number, body: string) {
    const serverMessage = parseJsonMessage(body);
    const message = serverMessage
      ? `${serverMessage}\n\nUrl: ${url}\n\n Status: ${status}\n\nBody:\n${body}`
      : `Error fetching ${url}. Status: ${status}. Body:\n\n${body}`;

    const err = new ApiError(message);
    err.name = `${apiName}${status}Error`;
    err.httpCode = status;
    err.body = body;
    err.serverMessage = serverMessage ?? undefined;

    if ('captureStackTrace' in Error) {
      (Error.captureStackTrace as any)(err, this.fromResponse);
    }

    return err;
  }

  static async fromResponse(apiName: string, response: Response) {
    const body = await response.clone().text();
    return this.fromResponseData(apiName, response.url, response.status, body);
  }

  static fromRequestError(apiName: string, response: Request, e: unknown) {
    const message = `Error fetching ${response.url}`;

    const err = new ApiError(message, { cause: e });
    err.name = `${apiName}RequestError`;

    if ('captureStackTrace' in Error) {
      (Error.captureStackTrace as any)(err, this.fromResponse);
    }

    return err;
  }

  static canRetry(error: Error) {
    return !(
      error instanceof ApiError &&
      error.httpCode &&
      error.httpCode >= 400 &&
      error.httpCode < 500
    );
  }
}

function parseJsonMessage(body: string) {
  try {
    const json = JSON.parse(body || '{}') as { message?: string } | undefined;
    const message = json && json.message;
    return typeof message === 'string' ? message : null;
  } catch {
    return null;
  }
}
